import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import AdminLayout from '@/components/admin/AdminLayout';
import {
  Users,
  Award,
  TrendingUp,
  Plane,
  Brain,
  MessageCircle,
  BookOpen
} from 'lucide-react';

interface DashboardMetric {
  title: string;
  value: number;
  icon: React.ReactNode;
  link: string;
  color: string;
  description: string;
}

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetric[]>([
    {
      title: 'Total Users',
      value: 0,
      icon: <Users className="h-8 w-8" />,
      link: '/administrator/users',
      color: 'bg-blue-500',
      description: 'Total registered users'
    },
    {
      title: 'Silver Plan',
      value: 0,
      icon: <Award className="h-8 w-8" />,
      link: '/administrator/silver-users',
      color: 'bg-gray-400',
      description: 'Silver plan customers'
    },
    {
      title: 'Gold Plan',
      value: 0,
      icon: <TrendingUp className="h-8 w-8" />,
      link: '/administrator/gold-users',
      color: 'bg-yellow-500',
      description: 'Gold plan customers'
    },
    {
      title: 'Study Abroad',
      value: 0,
      icon: <Plane className="h-8 w-8" />,
      link: '/administrator/study-abroad-insights',
      color: 'bg-purple-500',
      description: 'Study abroad prospects'
    },
    {
      title: 'Psychologist Bookings',
      value: 0,
      icon: <Brain className="h-8 w-8" />,
      link: '/administrator/psychologist-bookings',
      color: 'bg-green-500',
      description: 'Total psychologist sessions'
    },
    {
      title: 'Counselor Bookings',
      value: 0,
      icon: <MessageCircle className="h-8 w-8" />,
      link: '/administrator/counselor-bookings',
      color: 'bg-red-500',
      description: 'Total counselor sessions'
    },
    {
      title: 'Expert Bookings',
      value: 0,
      icon: <BookOpen className="h-8 w-8" />,
      link: '/administrator/expert-bookings',
      color: 'bg-indigo-500',
      description: 'Total domain expert sessions'
    }
  ]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch total users
        const { count: totalUsers } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true });

        // Fetch silver plan customers
        const { count: silverUsers } = await supabase
          .from('user_subscriptions')
          .select('*', { count: 'exact', head: true })
          .eq('package_type', 'silver');

        // Fetch gold plan customers
        const { count: goldUsers } = await supabase
          .from('user_subscriptions')
          .select('*', { count: 'exact', head: true })
          .eq('package_type', 'gold');

        // Fetch study abroad interests (with fallback counting method)
        const studyAbroadQuery = supabase
          .from('user_responses')
          .select('*', { count: 'exact' })
          .eq('question_id', 63)
          .neq('answer', 'Not Interested');
        
        // Execute the query
        const { count: studyAbroadUsers } = await studyAbroadQuery;

        // Add fallback count method
        const { data } = await supabase
          .from('user_responses')
          .select('id')
          .eq('question_id', 63)
          .neq('answer', 'Not Interested');

        const fallbackCount = data?.length || 0;

        // Use fallbackCount if studyAbroadUsers is undefined or null
        const finalStudyAbroadCount = studyAbroadUsers === null || studyAbroadUsers === undefined 
          ? fallbackCount 
          : studyAbroadUsers;

        // Fetch bookings by association type
        const { data: bookings } = await supabase
          .from('bookings')
          .select('association_id, associations:association_id(name)');

        // Count bookings by type
        let psychologistBookings = 0;
        let counselorBookings = 0;
        let expertBookings = 0;

        bookings?.forEach(booking => {
          const associationType = booking.associations?.name || '';
          if (associationType === 'Psychologist') {
            psychologistBookings++;
          } else if (associationType === 'Career Counselor') {
            counselorBookings++;
          } else if (associationType === 'Domain Expert') {
            expertBookings++;
          }
        });

        // Update metrics with fetched data
        setMetrics(prev => {
          const updated = [...prev];
          updated[0].value = totalUsers || 0;
          updated[1].value = silverUsers || 0;
          updated[2].value = goldUsers || 0;
          updated[3].value = finalStudyAbroadCount || 0; // Use the fallback count if needed
          updated[4].value = psychologistBookings;
          updated[5].value = counselorBookings;
          updated[6].value = expertBookings;
          return updated;
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <AdminLayout>
      <Helmet>
        <title>Admin Dashboard | Career Guide Kerala</title>
      </Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-gray-500">Overview of CareerGuideKerala.in platform metrics</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {metrics.map((metric, index) => (
            <Link key={index} to={metric.link}>
              <Card className="hover:shadow-md transition-shadow duration-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-medium">{metric.title}</CardTitle>
                  <CardDescription>{metric.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-full ${metric.color} text-white`}>
                      {metric.icon}
                    </div>
                    {loading ? (
                      <Skeleton className="h-10 w-16" />
                    ) : (
                      <div className="text-2xl font-bold">{metric.value}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
